import React from 'react';
import { Home, DollarSign, SquareAsterisk, Percent, FileText } from 'lucide-react';

/**
 * Компонент параметров недвижимости для расчета налога
 */
const PropertyParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  propertyTypes
}) => {
  return (
    <div className="mb-6 bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Home className="w-5 h-5 mr-2 text-green-500" />
        Параметры недвижимости
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Рыночная стоимость недвижимости */}
        <div>
          <label htmlFor="propertyCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-2 text-green-500" />
            Рыночная стоимость (₽)
          </label>
          <input
            type="text"
            id="propertyCost"
            name="propertyCost"
            value={formData.propertyCost}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.propertyCost ? 'input-error' : ''}`}
            placeholder="Введите стоимость недвижимости"
          />
          {errors.propertyCost && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.propertyCost}
            </p>
          )}
        </div>

        {/* Кадастровая стоимость */}
        <div>
          <label htmlFor="cadastralValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <FileText className="w-4 h-4 mr-2 text-green-500" />
            Кадастровая стоимость (₽)
          </label>
          <input
            type="text"
            id="cadastralValue"
            name="cadastralValue"
            value={formData.cadastralValue}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.cadastralValue ? 'input-error' : ''}`}
            placeholder="Введите кадастровую стоимость"
          />
          {errors.cadastralValue && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.cadastralValue}
            </p>
          )}
        </div>

        {/* Площадь недвижимости */}
        <div>
          <label htmlFor="propertyArea" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <SquareAsterisk className="w-4 h-4 mr-2 text-green-500" />
            Площадь (кв.м)
          </label>
          <input
            type="text"
            id="propertyArea"
            name="propertyArea"
            value={formData.propertyArea}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.propertyArea ? 'input-error' : ''}`}
            placeholder="Введите площадь недвижимости"
          />
          {errors.propertyArea && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.propertyArea}
            </p>
          )}
        </div>

        {/* Тип недвижимости */}
        <div>
          <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип недвижимости
          </label>
          <select
            id="propertyType"
            name="propertyType"
            value={formData.propertyType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.propertyType ? 'input-error' : ''}`}
          >
            {propertyTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.propertyType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.propertyType}
            </p>
          )}
        </div>

        {/* Тип владения */}
        <div>
          <label htmlFor="ownershipType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип владения
          </label>
          <select
            id="ownershipType"
            name="ownershipType"
            value={formData.ownershipType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.ownershipType ? 'input-error' : ''}`}
          >
            <option value="individual">Индивидуальная собственность</option>
            <option value="shared">Долевая собственность</option>
            <option value="joint">Совместная собственность</option>
          </select>
          {errors.ownershipType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.ownershipType}
            </p>
          )}
        </div>

        {/* Доля собственности */}
        {formData.ownershipType === 'shared' && (
          <div>
            <label htmlFor="ownershipShare" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Percent className="w-4 h-4 mr-2 text-green-500" />
              Доля в собственности (%)
            </label>
            <input
              type="text"
              id="ownershipShare"
              name="ownershipShare"
              value={formData.ownershipShare}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.ownershipShare ? 'input-error' : ''}`}
              placeholder="Введите долю в процентах"
            />
            {errors.ownershipShare && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.ownershipShare}
              </p>
            )}
          </div>
        )}

        {/* Дополнительные опции */}
        <div className="col-span-full">
          <div className="flex flex-col space-y-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="isOnlyProperty"
                checked={formData.isOnlyProperty}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Единственное жилье в собственности (налоговый вычет)
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Информация о налоге на роскошную недвижимость */}
      {parseFloat(formData.cadastralValue) >= 300000000 && formData.propertyType === 'apartment' && (
        <div className="mt-4 p-3 bg-green-100 dark:bg-green-900/40 rounded-lg text-sm text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
          <p className="font-medium">Информация о налоге на роскошную недвижимость:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Жилые помещения с кадастровой стоимостью более 300 млн ₽ облагаются по ставке 2%</li>
            <li>Для коммерческой недвижимости действуют повышенные ставки в зависимости от региона</li>
            <li>Единственное жилье может иметь налоговый вычет (уменьшение налоговой базы)</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PropertyParametersSection;